import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, CheckCircle2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { http, gradeLabel } from "@/lib/api";
import { LessonEditor } from "./LessonEditor";
import { dueLabel } from "@/components/dashboard/CourseWork";

export const AssignedLessons = ({ classId }) => {
  const [lessons, setLessons] = useState(null);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => http.get(`/classes/${classId}/lessons`).then((r) => setLessons(r.data)), [classId]);
  useEffect(() => { load(); }, [load]);

  const openEditor = (l) => { setEditing(l); setOpen(true); };
  const setStatus = async (l, status) => {
    await http.put(`/classes/${classId}/lessons/${l.id}`, { status });
    toast.success(status === "approved" ? "Approved & visible to students" : "Moved back to draft");
    load();
  };
  const del = async (l) => {
    if (!window.confirm(`Delete "${l.topic}"?`)) return;
    await http.delete(`/classes/${classId}/lessons/${l.id}`); toast.success("Lesson deleted"); load();
  };

  return (
    <div data-testid="assigned-lessons-tab">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
        <p className="text-sm text-stone-500">Review and edit Aria's lessons. Only <b className="text-[#2C4A3E]">approved</b> lessons appear to students as "Assigned"; they can still explore other topics freely.</p>
        <button onClick={() => openEditor(null)} data-testid="new-assigned-lesson-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#C87D53] text-white text-sm hover:bg-[#B36C44] transition-colors">
          <Plus className="w-4 h-4" /> New lesson
        </button>
      </div>
      <div className="space-y-3">
        {lessons?.map((l) => (
          <div key={l.id} className="flex flex-wrap items-center gap-4 rounded-2xl bg-white/60 border border-[#E6DEC3] p-4" data-testid={`assigned-lesson-row-${l.id}`}>
            <div className="flex-1 min-w-[200px]">
              <p className="font-display text-lg text-[#2C4A3E]">{l.topic}</p>
              <p className="text-xs text-stone-500">{l.subject_name} · {gradeLabel(l.grade)} · updated {new Date(l.updated_at).toLocaleDateString()}</p>
              {(l.points || l.due_at) && <p className="text-xs text-[#1F5C99] mt-0.5" data-testid={`assigned-lesson-due-${l.id}`}>{dueLabel(l)}{l.due_at && (l.late_ok ? ` · Late OK: ${l.late_pct}%${l.late_days ? ` up to ${l.late_days} day${l.late_days > 1 ? "s" : ""}` : ""}` : " · No late work")}</p>}
            </div>
            <span data-testid={`assigned-lesson-status-${l.id}`} className={`px-3 py-1 rounded-full text-xs font-medium ${l.status === "approved" ? "bg-[#6B9080] text-white" : "bg-[#F3DCC9] text-[#8a4b2a]"}`}>
              {l.status === "approved" ? "Approved" : "Draft"}
            </span>
            <div className="flex gap-1">
              <button onClick={() => openEditor(l)} title="Review & edit" data-testid={`assigned-lesson-edit-${l.id}`} className="p-2 rounded-full hover:bg-[#F1EADB] text-stone-600"><Pencil className="w-4 h-4" /></button>
              {l.status === "draft"
                ? <button onClick={() => setStatus(l, "approved")} title="Approve" data-testid={`assigned-lesson-approve-${l.id}`} className="p-2 rounded-full hover:bg-[#E3EBE5] text-[#2C4A3E]"><CheckCircle2 className="w-4 h-4" /></button>
                : <button onClick={() => setStatus(l, "draft")} title="Unapprove" data-testid={`assigned-lesson-unapprove-${l.id}`} className="p-2 rounded-full hover:bg-[#F1EADB] text-stone-600"><Undo2 className="w-4 h-4" /></button>}
              <button onClick={() => del(l)} title="Delete" data-testid={`assigned-lesson-delete-${l.id}`} className="p-2 rounded-full hover:bg-red-50 text-stone-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {lessons && !lessons.length && <p className="text-center text-stone-500 py-12" data-testid="assigned-lessons-empty">No lessons yet. Draft one with Aria, then review and approve it.</p>}
      </div>
      <LessonEditor classId={classId} lesson={editing} open={open} setOpen={setOpen} onSaved={load} />
    </div>
  );
};
